/**
 * MainVisualBackgroundEffect
 * 메인 비주얼(.no-main-visual--renew) 배경 이미지에 은은한 퍼짐(glow) + 미세 드리프트 이펙트 적용.
 * destroy() 호출 시 이펙트 제거 및 DOM/스타일 원복.
 */
export class MainVisualBackgroundEffect {
    static get selector() {
        return ".no-main-visual--renew";
    }

    static get imageSelector() {
        return ".no-main-visual__image";
    }

    static get imgSelector() {
        return ".no-main-visual__img";
    }

    /** 리빌 애니메이션 종료 후 드리프트 시작 지연(ms) */
    static get revealDelay() {
        return 4200;
    }

    constructor() {
        this.section = null;
        this.imageBlock = null;
        this.img = null;
        this.driftWrap = null;
        this.spreadOverlay = null;
        this.rafId = null;
        this.started = false;
        this._startTime = null;
    }

    /**
     * 이펙트 적용. 대상이 없으면 no-op.
     * @returns {boolean} 적용 여부
     */
    init() {
        this.section = document.querySelector(MainVisualBackgroundEffect.selector);
        if (!this.section) return false;

        this.imageBlock = this.section.querySelector(MainVisualBackgroundEffect.imageSelector);
        this.img = this.section.querySelector(MainVisualBackgroundEffect.imgSelector);
        if (!this.imageBlock || !this.img) return false;

        this._addSpreadOverlay();
        this._wrapImgForDrift();
        this.section.classList.add("no-main-visual--bg-effect");

        // 리빌 애니메이션 종료 후 은은한 드리프트 시작
        this._startDriftAfterReveal();
        return true;
    }

    _addSpreadOverlay() {
        if (this.spreadOverlay) return;
        this.spreadOverlay = document.createElement("div");
        this.spreadOverlay.setAttribute("aria-hidden", "true");
        this.spreadOverlay.className = "no-main-visual__spread-overlay";
        this.imageBlock.appendChild(this.spreadOverlay);
    }

    _wrapImgForDrift() {
        if (this.driftWrap) return;
        this.driftWrap = document.createElement("div");
        this.driftWrap.className = "no-main-visual__img-drift-wrap";
        this.img.parentNode.insertBefore(this.driftWrap, this.img);
        this.driftWrap.appendChild(this.img);
    }

    _startDriftAfterReveal() {
        const start = () => {
            this.started = true;
            this._startTime = null;
            this._tick();
        };
        if (MainVisualBackgroundEffect.revealDelay <= 0) {
            start();
        } else {
            setTimeout(start, MainVisualBackgroundEffect.revealDelay);
        }
    }

    /**
     * 드리프트: 시간 기반으로 translate + scale을 아주 조금씩 변경 (은은한 몰입감)
     */
    _tick() {
        if (!this.driftWrap) return;
        if (this._startTime == null) this._startTime = performance.now();
        const elapsed = (performance.now() - this._startTime) * 0.001; // 초 단위
        const period = 24; // 한 사이클(초)
        const progress = (elapsed % period) / period; // 0~1 반복
        const rad = progress * Math.PI * 2;

        // 진폭을 매우 작게 (은은하게)
        const translateX = Math.sin(rad) * 1.2;
        const translateY = Math.cos(rad * 0.7) * 1;
        const scale = 1 + Math.sin(rad * 1.3) * 0.012;

        this.driftWrap.style.transform = `translate(${translateX}%, ${translateY}%) scale(${scale})`;
        this.rafId = requestAnimationFrame(() => this._tick());
    }

    _stopDrift() {
        if (this.rafId != null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.started = false;
        if (this.driftWrap) {
            this.driftWrap.style.transform = "";
        }
    }

    /**
     * 이펙트 제거 및 DOM/스타일 원복. 여러 번 호출해도 안전.
     */
    destroy() {
        this._stopDrift();

        if (this.spreadOverlay && this.spreadOverlay.parentNode) {
            this.spreadOverlay.parentNode.removeChild(this.spreadOverlay);
            this.spreadOverlay = null;
        }

        if (this.driftWrap && this.img) {
            this.driftWrap.parentNode.insertBefore(this.img, this.driftWrap);
            this.driftWrap.parentNode.removeChild(this.driftWrap);
            this.driftWrap = null;
        }

        if (this.section) {
            this.section.classList.remove("no-main-visual--bg-effect");
        }
    }
}
